'use client'

import React, { useEffect, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import NextImage from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTenant } from '@/components/TenantLayout/Client'
import { PhiMask } from '@/components/PhiMask'
import {
  CLIENT_PATHS,
  CLIENT_PUBLIC_LEGAL_PATHS,
  SHARED_PATHS,
  isAuthPagePath,
} from '@/utilities/paths'
import { cn } from '@/utilities/ui'
import type { Media } from '@/payload-types'
import { HeaderRight } from './HeaderRight'

/** Mount target for print-only right-side header content (e.g. kit results title). */
export const PRINT_HEADER_RIGHT_ROOT_ID = 'print-header-right-root'

export function Header() {
  const { tenant } = useTenant()
  const pathname = usePathname()
  const logo = tenant?.logo && typeof tenant.logo === 'object' ? (tenant.logo as Media) : null
  const logoUrl = logo?.url
  const logoWidth = logo?.width ?? 190
  const logoHeight = logo?.height ?? 60
  const logoHref = tenant?.slug === 'client' ? CLIENT_PATHS.login : SHARED_PATHS.root
  const isClientTenant = tenant?.slug === 'client'
  const isAuthPath = isAuthPagePath(pathname)
  const isClientPublicContentPath = CLIENT_PUBLIC_LEGAL_PATHS.some(
    (path) => pathname === path || pathname?.startsWith(`${path}/`),
  )
  const useClientLoggedOutSizing = isClientTenant && (isAuthPath || isClientPublicContentPath)

  // Always render default header, but only show auth controls when user is logged in.
  return (
    <>
      <header
        data-testid="patient-header"
        className={cn(
          'flex shrink-0 items-center links-inherit-color',
          'h-16',
          'print:hidden',
          isClientTenant
            ? 'bg-cp-dark-blue text-white border-b border-b-light-grey'
            : 'bg-tenant-top-nav-background text-tenant-top-nav-foreground shadow-bottom z-1',
        )}
      >
        <div className="w-full flex items-center justify-between px-4 sm:px-5 gap-2">
          {/* Left: Logo */}
          <div className="flex items-center gap-10 shrink-0" data-testid="header-logo-placeholder">
            {logoUrl && (
              <Link
                href={logoHref}
                aria-label="Go to home"
                data-testid="go-to-home-link"
                className="logo-container block"
              >
                <PhiMask as="span" className="inline-block" maskPosthog={false}>
                  <NextImage
                    src={logoUrl}
                    alt={`${tenant?.name ?? 'Logo'}`}
                    className={cn(
                      'object-contain w-auto max-w-[140px] sm:max-w-[190px]',
                      useClientLoggedOutSizing ? 'h-8' : 'h-auto max-h-[48px] sm:max-h-[60px]',
                    )}
                    width={logoWidth}
                    height={logoHeight}
                    priority={true}
                  />
                </PhiMask>
              </Link>
            )}
          </div>

          <HeaderRight />
        </div>
      </header>
      <PrintHeader name={tenant?.name} />
    </>
  )
}

function PrintHeader({ name }: Readonly<{ name?: string | null }>) {
  if (!name) return null

  return (
    <header
      data-testid="print-header"
      className="hidden print:flex items-center justify-between gap-4 w-full pb-4"
    >
      <p className="text-base font-medium shrink-0">{name}</p>
      <div
        ref={setPrintHeaderRightSlot}
        id={PRINT_HEADER_RIGHT_ROOT_ID}
        data-testid="print-header-right"
        className="ml-auto min-w-0 text-right text-sm"
      />
    </header>
  )
}

type PrintHeaderRightSlotListener = () => void

let printHeaderRightSlot: HTMLElement | null = null
const printHeaderRightSlotListeners = new Set<PrintHeaderRightSlotListener>()

function subscribePrintHeaderRightSlot(onStoreChange: PrintHeaderRightSlotListener) {
  printHeaderRightSlotListeners.add(onStoreChange)
  return () => {
    printHeaderRightSlotListeners.delete(onStoreChange)
  }
}

function getPrintHeaderRightSlot() {
  return printHeaderRightSlot
}

function setPrintHeaderRightSlot(el: HTMLElement | null) {
  printHeaderRightSlot = el
  printHeaderRightSlotListeners.forEach((listener) => listener())
}

/**
 * Moves the page title (`Kit Results - … | … Portal`) to the right of our print
 * header. Chrome centers `document.title` in print chrome and pages cannot
 * reposition that line, so we clear the title while printing and render it here.
 */
export function PrintHeaderRight({
  title,
  printDocumentTitle = '\u00A0',
}: Readonly<{
  title: string
  /** Title while the print dialog is open; restored afterwards. */
  printDocumentTitle?: string
}>) {
  const slot = useSyncExternalStore(
    subscribePrintHeaderRightSlot,
    getPrintHeaderRightSlot,
    () => null,
  )

  useEffect(() => {
    let titleBeforeBlank: string | null = null

    const handleBeforePrint = () => {
      if (document.title !== printDocumentTitle) {
        titleBeforeBlank = document.title
      }
      document.title = printDocumentTitle
    }
    const handleAfterPrint = () => {
      if (titleBeforeBlank === null) return
      document.title = titleBeforeBlank
      titleBeforeBlank = null
    }
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      if (document.title === printDocumentTitle && titleBeforeBlank !== null) {
        document.title = titleBeforeBlank
      }
    }
  }, [printDocumentTitle])

  if (!slot || !title) return null
  return createPortal(
    <PhiMask as="span" className="block text-right text-primary-action-hover-color">
      <span data-testid="print-header-document-title">{title}</span>
    </PhiMask>,
    slot,
  )
}
